import React, { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { ConfirmationModal, Table } from 'components/Ui'
import {
    deleteApiRecord,
    updateApiStatus,
} from 'services/TransportProviderUserService'
import { Success } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
import { status } from 'components/helper'
injectReducer('transportProviderUser', reducer)

export default function TransportProviderUser() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const { data, tableData, loading } = useSelector(
        (state) => state.transportProviderUser.data,
    )

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.checkBox,
    ])

    const fetchData = () => dispatch(getData({ ...tableData }))

    const handleDelete = (id) => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                fetchData()
            })
            .catch((error) => error)
    }

    const handleStatus = (id, data) => {
        updateApiStatus(id, data)
            .then(() => {
                Success('Status Updated Successfully!')
                fetchData()
            })
            .catch((error) => error)
    }

    const columns = useMemo(
        () => [
            {
                field: 'company',
                header: 'Company',
                className: 'min-w-[150px]',
                renderCell: (row) => row?.assignment?.company_name,
            },
            {
                field: 'client',
                header: 'Client Company',
                className: 'min-w-[150px]',
                renderCell: (row) => row?.assignment?.client,
            },
            {
                field: 'transport_provider',
                header: 'Transport Provider',
                className: 'min-w-[150px]',
                renderCell: (row) => row?.assignment?.transport_provider_name,
            },
            {
                field: 'service_name',
                header: 'Service',
                className: 'min-w-[150px]',
                renderCell: (row) => row?.assignment?.service_name,
            },
            {
                field: 'name',
                header: 'Name',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    `${row?.title}. ${row?.user?.first_name} ${row?.user?.last_name}`,
            },
            {
                field: 'email',
                header: 'Email',
                className: 'min-w-[150px]',
                renderCell: (row) => row?.user?.email,
            },
            {
                field: 'is_active',
                header: 'Status',
                renderCell: (row) => {
                    return checkPermission('company-admin', 'edit') ? (
                        <ConfirmationModal
                            onConfirm={() => {
                                let data = {
                                    company: row?.assignment?.company_id,
                                    service: row?.assignment?.service,
                                    transport_provider:
                                        row?.assignment?.transport_provider,
                                    client: row?.assignment?.client
                                        ? row?.assignment?.client_assign_id
                                        : '',
                                    title: row.title,
                                    is_active: !row.is_active,
                                    user: { ...row.user },
                                }
                                handleStatus(row.id, data)
                            }}
                        >
                            {status(row.is_active)}
                        </ConfirmationModal>
                    ) : (
                        status(row.is_active)
                    )
                },
            },
            {
                header: 'Action',
                field: 'action',
                className: 'px-4',
                renderCell: (row) => {
                    return (
                        <div className="flex items-center gap-3">
                            {checkPermission(
                                'transport-provider-user',
                                'edit',
                            ) ? (
                                <Tooltip title={'Edit'}>
                                    <Link
                                        to={`/transport-service-provider/transport-provider-user/${row.id}/edit`}
                                        className="hover:text-primary"
                                    >
                                        <HiOutlinePencil size={20} />
                                    </Link>
                                </Tooltip>
                            ) : null}
                            {checkPermission(
                                'transport-provider-user',
                                'delete',
                            ) ? (
                                <Tooltip title={'Delete'}>
                                    <ConfirmationModal
                                        message={
                                            'Are you sure you want to delete this record!'
                                        }
                                        onConfirm={() => {
                                            handleDelete(row.id)
                                        }}
                                    >
                                        <span className="hover:text-danger">
                                            <HiOutlineTrash size={20} />
                                        </span>
                                    </ConfirmationModal>
                                </Tooltip>
                            ) : null}
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.checkBox,
        ],
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Transport Provider User
                </h2>
            </div>
            <Table
                loading={loading}
                datas={data}
                columns={columns}
                add_new={
                    checkPermission('transport-provider-user', 'create')
                        ? '/transport-service-provider/transport-provider-user/create'
                        : ''
                }
                pagingData={tableData}
                onChange={(e) => dispatch(setTableData(e))}
            />
        </>
    )
}
